import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { Container, Row, Col, Card, Button, Form, Alert, Table, Badge } from 'react-bootstrap';
import { formatPaymentMethod } from '../utils/paymentMethodFormatter';
import { CustomerLink, VehicleLink, PaymentLink } from './EntityLinks';
import { renderPaymentMethodOptions } from '../utils/paymentMethodOptions';
import { StatusBadge } from './shared/StatusBadge';
import editIcon from '../assets/icons/edit.svg';
import saveIcon from '../assets/icons/save.svg';
import closeIcon from '../assets/icons/close.svg';
import addIcon from '../assets/icons/add.svg';
import trashIcon from '../assets/icons/trash.svg';

/**
 * RTODetail component displays RTO agreement details, charges, and payments
 */
export function RTODetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiCall, user } = useOutletContext();
  const [rto, setRto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    amount: '',
    payment_method: 'cash_app',
    payment_date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [paymentError, setPaymentError] = useState(null);
  const [generatingContract, setGeneratingContract] = useState(false);

  useEffect(() => {
    const loadRTO = async () => {
      try {
        const response = await apiCall(`${API_BASE_URL}/rto-agreements/${id}`);
        const rtoData = response.data || response;
        if (rtoData) {
          setRto(rtoData);
          setFormData({
            recurring_amount: rtoData.recurring_amount,
            frequency: rtoData.frequency,
            status: rtoData.status,
            notes: rtoData.notes || '',
          });
        } else {
          setError('RTO agreement not found');
        }
      } catch (err) {
        setError('Failed to load RTO agreement: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadRTO();
  }, [id, apiCall]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await apiCall(
        `${API_BASE_URL}/rto-agreements/${id}`,
        {
          method: 'PUT',
          body: JSON.stringify(formData),
        }
      );

      if (response.success || response.data) {
        // Refresh RTO data
        const refreshRes = await apiCall(`${API_BASE_URL}/rto-agreements/${id}`);
        setRto(refreshRes.data || refreshRes);
        setIsEditing(false);
        setSuccessMessage('RTO agreement updated successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(response.error || 'Failed to update RTO agreement');
      }
    } catch (err) {
      setError('Error updating RTO agreement: ' + err.message);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setPaymentError(null);

    if (!paymentFormData.amount || parseFloat(paymentFormData.amount) <= 0) {
      setPaymentError('Payment amount must be greater than zero.');
      return;
    }

    try {
      const response = await apiCall(
        `${API_BASE_URL}/payments`,
        {
          method: 'POST',
          body: JSON.stringify({
            customer_id: rto.customer_id,
            rto_agreement_id: parseInt(id),
            amount: parseFloat(paymentFormData.amount),
            payment_method: paymentFormData.payment_method,
            payment_date: paymentFormData.payment_date,
            notes: paymentFormData.notes || `RTO payment for agreement #${id}`,
            agent_id: user?.id,
          }),
        }
      );

      if (response.success || response.data) {
        setShowPaymentForm(false);
        setPaymentFormData({
          amount: '',
          payment_method: 'cash_app',
          payment_date: new Date().toISOString().split('T')[0],
          notes: '',
        });
        alert('Payment recorded successfully');

        // Refresh RTO data
        const refreshRes = await apiCall(`${API_BASE_URL}/rto-agreements/${id}`);
        setRto(refreshRes.data || refreshRes);
      } else {
        setPaymentError(response.error || 'Failed to record payment');
      }
    } catch (err) {
      setPaymentError('Error recording payment: ' + err.message);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this RTO agreement?')) {
      return;
    }

    try {
      const response = await apiCall(
        `${API_BASE_URL}/rto-agreements/${id}`,
        { method: 'DELETE' }
      );

      if (response.success) {
        alert('RTO agreement cancelled successfully');
        navigate('/dashboard/rto');
      } else {
        setError(response.error || 'Failed to cancel RTO agreement');
      }
    } catch (err) {
      setError('Error cancelling RTO agreement: ' + err.message);
    }
  };

  const handleHardDelete = async () => {
    if (!window.confirm('Are you sure you want to PERMANENTLY DELETE this RTO agreement? This will also delete all charges and payments. This cannot be undone!')) {
      return;
    }

    try {
      const response = await apiCall(
        `${API_BASE_URL}/rto-agreements/${id}?hard=true`,
        { method: 'DELETE' }
      );

      if (response.success) {
        alert('RTO agreement permanently deleted');
        navigate('/dashboard/rto');
      } else {
        setError(response.error || 'Failed to delete RTO agreement');
      }
    } catch (err) {
      setError('Error deleting RTO agreement: ' + err.message);
    }
  };

  const handleGenerateContract = async () => {
    setGeneratingContract(true);
    setError(null);
    try {
      const response = await apiCall(
        `${API_BASE_URL}/rto-agreements/${id}/generate-contract`,
        { method: 'POST' }
      );

      if (response.data?.pdfBase64) {
        const byteCharacters = atob(response.data.pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        setError(response.error || 'Failed to generate contract');
      }
    } catch (err) {
      setError('Error generating contract: ' + err.message);
    } finally {
      setGeneratingContract(false);
    }
  };

  const formatFrequency = (frequency) => {
    switch (frequency) {
      case 'weekly': return 'Weekly';
      case 'biweekly': return 'Bi-Weekly';
      case 'monthly': return 'Monthly';
      default: return frequency;
    }
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div>Loading RTO agreement details...</div>
      </Container>
    );
  }

  if (error && !rto) {
    return (
      <Container fluid className="py-4">
        <Button variant="link" onClick={() => navigate('/dashboard/rto')} className="p-0 mb-3">
          ← Back to RTO Agreements
        </Button>
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 domain-transactions">
      <Row className="mb-3">
        <Col>
          <Button variant="link" onClick={() => navigate('/dashboard/rto')} className="p-0">
            ← Back to RTO Agreements
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <h2>
            RTO Agreement #{rto.id}
            <span className="ms-3"><StatusBadge status={rto.status} type="rto" delinquencyStatus={rto.delinquency_status} /></span>
          </h2>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}

      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h3>Agreement Details</h3>
            </Card.Header>
            <Card.Body>
              {!isEditing ? (
                <>
                  <Row>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Customer</strong>
                      <CustomerLink customerId={rto.customer_id} customerName={rto.customer_name} />
                      <br />
                      <small className="text-muted">{rto.customer_phone}</small>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Vehicle</strong>
                      <VehicleLink
                        vehicleId={rto.vehicle_id}
                        vehicleInfo={`${rto.vehicle_year} ${rto.vehicle_make} ${rto.vehicle_model}`}
                      />
                      <br />
                      <small className="text-muted">{rto.vehicle_license_plate} | VIN: {rto.vehicle_vin}</small>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Total Sale Price</strong>
                      <span>${parseFloat(rto.total_sale_price).toLocaleString()}</span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Down Payment</strong>
                      <span>${parseFloat(rto.down_payment).toLocaleString()}</span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Recurring Payment</strong>
                      <span>${parseFloat(rto.recurring_amount).toFixed(0)} / {formatFrequency(rto.frequency)}</span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Start Date</strong>
                      <span>{rto.start_date?.split('T')[0]}</span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Next Payment Due</strong>
                      <span>{rto.next_payment_due_date?.split('T')[0]}</span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Store</strong>
                      <span>{rto.store_name}</span>
                    </Col>
                    {rto.notes && (
                      <Col md={12} className="mb-3">
                        <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Notes</strong>
                        <span>{rto.notes}</span>
                      </Col>
                    )}
                  </Row>
                  {user?.role !== 'auditor' && (
                    <>
                      <Button
                        variant="success"
                        onClick={handleGenerateContract}
                        disabled={generatingContract}
                        className="me-2"
                      >
                        {generatingContract ? 'Generating...' : 'Generate Agreement'}
                      </Button>
                      <Button variant="primary" onClick={() => setIsEditing(true)} className="me-2">
                        <img src={editIcon} alt="" className="icon-btn" />
                        Edit Agreement
                      </Button>
                      {rto.status !== 'cancelled' && (
                        <Button variant="warning" onClick={handleCancel} className="me-2">
                          <img src={closeIcon} alt="" className="icon-btn" />
                          Cancel Agreement
                        </Button>
                      )}
                      <Button variant="danger" onClick={handleHardDelete}>
                        <img src={trashIcon} alt="" className="icon-btn" />
                        Delete Permanently
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <Form onSubmit={handleSave}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Recurring Payment</Form.Label>
                        <Form.Control
                          type="number"
                          name="recurring_amount"
                          step="0.01"
                          value={formData.recurring_amount}
                          onChange={handleFormChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Frequency</Form.Label>
                        <Form.Select
                          name="frequency"
                          value={formData.frequency}
                          onChange={handleFormChange}
                        >
                          <option value="weekly">Weekly</option>
                          <option value="biweekly">Bi-Weekly</option>
                          <option value="monthly">Monthly</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          name="status"
                          value={formData.status}
                          onChange={handleFormChange}
                        >
                          <option value="active">Active</option>
                          <option value="completed">Completed</option>
                          <option value="defaulted">Defaulted</option>
                          <option value="cancelled">Cancelled</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="notes"
                          value={formData.notes}
                          onChange={handleFormChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Button type="submit" variant="primary" className="me-2">
                    <img src={saveIcon} alt="" className="icon-btn" />
                    Save Changes
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setIsEditing(false);
                      setFormData({
                        recurring_amount: rto.recurring_amount,
                        frequency: rto.frequency,
                        status: rto.status,
                        notes: rto.notes || '',
                      });
                    }}
                  >
                    <img src={closeIcon} alt="" className="icon-btn" />
                    Cancel
                  </Button>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>
              <h3>Balance Summary</h3>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6} className="mb-3">
                  <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Total Paid</strong>
                  <span style={{ color: '#388e3c', fontWeight: 'bold', fontSize: '1.2rem' }}>
                    ${parseFloat(rto.total_paid).toLocaleString()}
                  </span>
                </Col>
                <Col md={6} className="mb-3">
                  <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Balance Remaining</strong>
                  <span style={{ color: '#d32f2f', fontWeight: 'bold', fontSize: '1.2rem' }}>
                    ${parseFloat(rto.balance_remaining).toLocaleString()}
                  </span>
                </Col>
                <Col md={6} className="mb-3">
                  <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Amount Owed (Past Due)</strong>
                  <span style={{ color: parseFloat(rto.total_owed) > 0 ? '#d32f2f' : '#388e3c', fontWeight: 'bold' }}>
                    ${parseFloat(rto.total_owed || 0).toFixed(2)}
                  </span>
                </Col>
                <Col md={6} className="mb-3">
                  <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>Progress</strong>
                  <div style={{ backgroundColor: '#e0e0e0', borderRadius: '4px', height: '20px', overflow: 'hidden' }}>
                    <div
                      style={{
                        backgroundColor: '#3b82f6',
                        height: '100%',
                        width: `${Math.min(100, (parseFloat(rto.total_paid) / parseFloat(rto.total_sale_price)) * 100)}%`,
                        transition: 'width 0.3s',
                      }}
                    />
                  </div>
                  <small className="text-muted">
                    {((parseFloat(rto.total_paid) / parseFloat(rto.total_sale_price)) * 100).toFixed(1)}% complete
                  </small>
                </Col>
              </Row>

              {rto.status === 'active' && (
                <Button
                  variant="primary"
                  onClick={() => setShowPaymentForm(!showPaymentForm)}
                  className="mt-2"
                >
                  <img src={addIcon} alt="" className="icon-btn" />
                  {showPaymentForm ? 'Cancel Payment' : 'Record Payment'}
                </Button>
              )}

              {showPaymentForm && (
                <Card className="mt-3">
                  <Card.Body>
                    <Form onSubmit={handlePaymentSubmit}>
                      {paymentError && <Alert variant="danger">{paymentError}</Alert>}
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Amount *</Form.Label>
                            <Form.Control
                              type="number"
                              step="0.01"
                              value={paymentFormData.amount}
                              onChange={(e) => setPaymentFormData({ ...paymentFormData, amount: e.target.value })}
                              required
                              placeholder={rto.total_owed > 0 ? rto.total_owed : rto.recurring_amount}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Payment Method</Form.Label>
                            <Form.Select
                              value={paymentFormData.payment_method}
                              onChange={(e) => setPaymentFormData({ ...paymentFormData, payment_method: e.target.value })}
                            >
                              {renderPaymentMethodOptions()}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Payment Date</Form.Label>
                            <Form.Control
                              type="date"
                              value={paymentFormData.payment_date}
                              onChange={(e) => setPaymentFormData({ ...paymentFormData, payment_date: e.target.value })}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Notes</Form.Label>
                            <Form.Control
                              type="text"
                              value={paymentFormData.notes}
                              onChange={(e) => setPaymentFormData({ ...paymentFormData, notes: e.target.value })}
                              placeholder="Optional"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Button type="submit" variant="primary">
                        Record Payment
                      </Button>
                    </Form>
                  </Card.Body>
                </Card>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charges Section */}
      <Card className="mb-4">
        <Card.Header>
          <h3>Payment Schedule / Charges ({rto.charges?.length || 0})</h3>
        </Card.Header>
        <Card.Body className="p-0">
          {rto.charges && rto.charges.length > 0 ? (
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>Due Date</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Paid</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rto.charges.map(charge => (
                  <tr key={charge.id}>
                    <td>{charge.due_date?.split('T')[0]}</td>
                    <td>{charge.type.charAt(0).toUpperCase() + charge.type.slice(1)}</td>
                    <td>${parseFloat(charge.amount).toFixed(2)}</td>
                    <td>${parseFloat(charge.paid_amount || 0).toFixed(2)}</td>
                    <td>
                      <StatusBadge
                        status={charge.is_paid ? 'paid' : parseFloat(charge.paid_amount) > 0 ? 'partial' : 'unpaid'}
                        type="payment"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-4 text-muted">No charges yet</div>
          )}
        </Card.Body>
      </Card>

      {/* Payments Section */}
      <Card className="mb-4">
        <Card.Header>
          <h3>Payment History ({rto.payments?.length || 0})</h3>
        </Card.Header>
        <Card.Body className="p-0">
          {rto.payments && rto.payments.length > 0 ? (
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {rto.payments.map(payment => (
                  <tr key={payment.id}>
                    <td>
                      <PaymentLink paymentId={payment.id}>
                        #{payment.id}
                      </PaymentLink>
                    </td>
                    <td>{payment.payment_date?.split('T')[0]}</td>
                    <td>${parseFloat(payment.amount).toFixed(2)}</td>
                    <td>{formatPaymentMethod(payment.payment_method)}</td>
                    <td>{payment.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-4 text-muted">No payments yet</div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default RTODetail;
